import { isEmpty, isPlainObject } from 'lodash';

import type { JSONData, JSONObject, JSONValue } from './types';

type CaseTransformer = (input: string | undefined) => string;

function isJSONObject(sourceObj: JSONData | string | number | boolean): sourceObj is JSONObject {
  return isPlainObject(sourceObj);
}

function isObjectOrArray(value: JSONData): value is JSONData {
  return (Array.isArray(value) || isJSONObject(value)) && !isEmpty(value);
}

export function recursiveCaseTransform<T extends JSONData | JSONValue>(
  sourceObj: JSONData | number | string | boolean,
  caseTransformFn: CaseTransformer
): T {
  // make recursive call if the sourceObj is an Array
  if (Array.isArray(sourceObj)) {
    return sourceObj.map((x: number | string | JSONObject) => recursiveCaseTransform<T>(x, caseTransformFn)) as T;
  }

  // early exit for recursive calls if the sourceObj is NOT a plain object
  // i.e. return sourceObj if it's a primitive (string, number, null, boolean, etc)
  if (!isJSONObject(sourceObj)) {
    return sourceObj as T;
  }

  const result: JSONObject = {};

  Object.keys(sourceObj).forEach((oldKey: string) => {
    const newKey: string = caseTransformFn(oldKey);
    const value: JSONData = sourceObj[oldKey] as JSONData;

    if (isObjectOrArray(value)) {
      result[newKey] = recursiveCaseTransform(value, caseTransformFn);
    } else {
      result[newKey] = value;
    }
  });

  return result as T;
}
